import { Radio } from "lucide-react";
import { polarityBg, tagMeta } from "../lib/taxonomy";
import type { LiveTagEvent } from "../hooks/useLiveStream";

type Props = { events: LiveTagEvent[]; connected: boolean };

export function LiveTagStream({ events, connected }: Props) {
  return (
    <section className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden">
      <header className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-brand-400" />
          <h2 className="text-sm font-semibold">Live tag stream</h2>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">
          {connected ? "subscribed · SSE" : "disconnected"}
        </span>
      </header>

      {events.length === 0 ? (
        <div className="p-10 text-center text-zinc-500 text-sm">
          {connected
            ? "Waiting for the first k-thresholded bucket to be released..."
            : "Start the device simulator to see live tag releases."}
        </div>
      ) : (
        <ul className="divide-y divide-zinc-800/70 max-h-[520px] overflow-auto">
          {events.map((e) => {
            const meta = tagMeta(e.tag_id);
            return (
              <li key={e.uid} className="slide-in p-3 flex items-center gap-3">
                <div className={`px-2 py-1 rounded-md text-xs border ${polarityBg(meta.polarity)}`}>
                  {meta.label}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-zinc-400 truncate">
                    <span className="font-mono">{e.planogram_id}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    device <span className="font-mono">{e.device_id}</span> ·
                    fw <span className="font-mono">{e.firmware_version}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold tabular-nums">×{e.count}</div>
                  <div className="text-[10px] text-zinc-500 tabular-nums">
                    conf {e.avg_confidence.toFixed(2)}
                  </div>
                </div>
                <div className="text-[10px] text-zinc-600 w-16 text-right tabular-nums">
                  {timeAgo(e.ingested_at)}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function timeAgo(iso: string): string {
  const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diffSec < 60) return `${Math.max(0, Math.round(diffSec))}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  return `${Math.round(diffSec / 3600)}h ago`;
}
